import {
  bank_account, loan,
  ManualName,
  receipt,
  select_package_configuration_key,
} from '../../utils/DictionaryManagementUtils';
import { getFlowCode } from '../Utils/GeneralUtills';

export default function stepsGoConfigFlow(configurations, service) {
  const { screens_ordering } = service;
  return getNextStepsByOrder(service, screens_ordering, configurations);
}

/*
    account_info
*/
const stepAccountTransactionsDetails = (service, screensOrdering, configurations, stepName) => ({
  name: getStepName('stepAccountTransactionsDetails', service),
  screen: 'accountTransactions',
  steps: service.transactions.enable_more_details_per_transaction ? [stepAccountTransactionDetails(service, screensOrdering, configurations, stepName)]
    : getNextStepsByOrder(service, screensOrdering, configurations, stepName),
});

const stepAccountTransactionDetails = (service, screensOrdering, configurations, stepName) => ({
  name: getStepName('stepAccountTransactionDetails', service),
  screen: 'accountTransactionDetails',
  steps: [stepConfirmAccountTransactionAction(service, screensOrdering, configurations, stepName)],
});

const stepConfirmAccountTransactionAction = (service, screensOrdering, configurations, stepName) => ({
  name: getStepName('stepConfirmAccountTransactionAction', service),
  screen: 'confirmAccountTransactionAction',
  steps: [stepAccountTransactionActionSuccess(service, screensOrdering, configurations, stepName)],
});

const stepAccountTransactionActionSuccess = (service) => ({
  name: getStepName('stepAccountTransactionActionSuccess', service),
  screen: 'transactionSuccess',
  steps: service.transactions.show_link_transactions_list
    ? [{
      name: getStepName('stepAccountTransactionsDetails', service),
      actionPath: 'accountBalancePath',
    }]
    : [],
});

/*
    pay_bills
*/
const stepSelectBill = (service, screensOrdering, configurations) => {
  const confirmation_ordering = ['confirmation_bill', 'success'];
  const steps = [addActionPath(getNextStep(service, confirmation_ordering, configurations, 'SelectBill'), 'selectBillPath')];
  if (service.select_bill.enable_customer_to_bill_code_manually) {
    steps.push(addActionPath(stepSelectPayBillInputMethod(service, screensOrdering, configurations, 'SelectBill'),
      'selectPayBillInputMethodPath'));
  }
  if (service.select_bill.show_account_balance) {
    steps.push(addActionPath(selectTransactionAccountPayBill(service, screensOrdering, configurations, 'SelectBill'), 'selectPayingAccountPath'));
  }
  return ({
    name: getStepName('stepSelectBill', service),
    screen: 'selectBill',
    steps,
  });
};

const stepSelectPayBillInputMethod = (service, screensOrdering, configurations, stepName, returnStep) => {
  const steps = [];
  if (service.select_bill.enable_customer_to_bill_code_manually) {
    steps.push(addActionPath(stepBarcodeInput(service, screensOrdering, configurations, stepName, returnStep), 'manualBillPath'));
    if (service.select_bill.enable_customer_to_scan_barcode) {
      steps.push(addActionPath(stepScanBarcode(service, screensOrdering, configurations, stepName, returnStep), 'scanBillPath'));
    }
  }
  return ({
    name: getStepName('stepSelectPayBillInputMethod', service),
    screen: 'selectPayBillInputMethod',
    steps,
  });
};

const stepScanBarcode = (service, screensOrdering, configurations, stepName, returnStep) => ({
  name: getStepName('stepScanBarcode', service),
  screen: 'barcodeScan',
  steps: getNextStepsByOrder(service, screensOrdering, configurations, `${stepName}BarcodeScan`, returnStep),
});

const stepBarcodeInput = (service, screensOrdering, configurations, stepName, returnStep) => ({
  name: getStepName('stepBarcodeInput', service),
  screen: 'barcodeInput',
  steps: getNextStepsByOrder(service, screensOrdering, configurations, `${stepName}BarcodeInput`, returnStep),
});

const selectTransactionAccountPayBill = (service, screensOrdering, configurations, stepName) => ({
  name: getStepName('selectTransactionAccount', service, stepName),
  screen: 'selectTransactionAccount',
  steps: [{ name: getStepName('stepSelectBill', service) }],
});

const stepBillConfirmation = (service, screensOrdering, configurations, stepName, returnStep) => ({
  name: getStepName('stepConfirmation', service, stepName),
  screen: 'billConfirmation',
  steps: getNextStepsByOrder(service, screensOrdering, configurations, stepName, returnStep),
});

/*
* Transactions Flows
* */
const stepTransferSelectRecipient = (service, screensOrdering, configurations, stepName) => {
  const { recipient } = service;
  if (!recipient.customer_should_select_recipient_request || (!recipient.select_from_recipients_list && !recipient.add_recipient_manually)) {
    return getNextStep(service, screensOrdering, configurations, stepName);
  }
  if (recipient.is_multiple_recipient) {
    return stepTransferSelectRecipientMultiple(service, screensOrdering, configurations, stepName);
  }
  if (!recipient.select_from_recipients_list) {
    return stepSelectPaymentMethod(service, screensOrdering, configurations, stepName);
  }

  const steps = [addActionPath(getNextStep(service, screensOrdering, configurations, stepName), 'selectRecipientPath')];
  if (recipient.add_recipient_manually) {
    steps.push(addActionPath(stepSelectPaymentMethod(service, screensOrdering, configurations, stepName), 'manualInputPath'));
  }
  return ({
    name: getStepName('stepTransferSelectRecipient', service, stepName),
    screen: 'selectRecipient',
    steps,
  });
};

const stepTransferSelectRecipientMultiple = (service, screensOrdering, configurations, stepName) => {
  const steps = [stepAmountPerParticipant(service, screensOrdering, configurations, stepName)];
  const { recipient } = service;

  const stepNameCurrent = getStepName('stepTransferSelectRecipient', service, stepName);

  if (recipient.add_recipient_manually) {
    steps.push(addActionPath(
      stepSelectPaymentMethod(service, screensOrdering, configurations, stepName, { name: stepNameCurrent }), 'manualInputPathSplit',
    ));
  }
  return ({
    name: stepNameCurrent,
    screen: 'selectRecipientMultiple',
    steps,
  });
};

const stepAmountPerParticipant = (service, screensOrdering, configurations, stepName) => ({
  name: getStepName('stepAmountPerParticipant', service, stepName),
  screen: 'amountPerParticipant',
  steps: getNextStepsByOrder(service, screensOrdering, configurations, `${stepName}AmountPerParticipant`),
});

const stepSelectPaymentMethod = (service, screensOrdering, configurations, stepName, returnStep) => {
  const { recipient } = service;
  const selectedManualRecipients = recipient.screens_ordering.filter((manual_key) => recipient.manual_recipients[manual_key].selected);

  if (selectedManualRecipients.length === 1) {
    const manual_key = selectedManualRecipients[0];
    return stepTransferInput(service, screensOrdering, configurations, `${stepName}${ManualName[manual_key]}`, ManualName[manual_key], returnStep);
  }

  const steps = selectedManualRecipients.map((manual_key) => addActionPath(
    stepTransferInput(service, screensOrdering, configurations, `${stepName}${ManualName[manual_key]}`, ManualName[manual_key], returnStep),
    `${ManualName[manual_key]}Path`,
  ));

  return ({
    name: getStepName('stepSelectPaymentMethod', service, stepName),
    screen: 'selectPaymentMethod',
    steps,
  });
};

const stepTransferInput = (service, screensOrdering, configurations, stepName, manualName, returnStep) => {
  if (manualName === ManualName[bank_account]) {
    return stepTransferAccountNumberInput(service, screensOrdering, configurations, stepName, manualName, returnStep);
  }
  return ({
    name: getStepName('stepTransferInput', service, stepName),
    screen: `${manualName}Input`,
    steps: [returnStep || getNextStep(service, screensOrdering, configurations, `${stepName}Transfer`)],
  });
};

const stepTransferAccountNumberInput = (service, screensOrdering, configurations, stepName, manualName, returnStep) => {
  const { recipient } = service;
  const manual_recipient = recipient.manual_recipients.bank_account;
  const steps = recipient.is_multiple_recipient
    ? [addActionPath(returnStep, 'manualInputAmountPath')]
    : [addActionPath(getNextStep(service, screensOrdering, configurations, `${stepName}Transfer`), 'manualInputAmountPath')];

  const stepNameCurrent = getStepName('stepTransferInput', service, stepName);
  if (manual_recipient.manual_fields_list.bank && manual_recipient.search_from_list_bank) {
    steps.push(addActionPath(stepSelectAccountBank(service, screensOrdering, configurations, stepName, { name: stepNameCurrent }),
      'manualInputSelectBankPath'));
  }
  if (manual_recipient.manual_fields_list.branch_name && manual_recipient.search_from_list_branch_name) {
    steps.push(addActionPath(stepSelectAccountBranch(service, screensOrdering, configurations, stepName, { name: stepNameCurrent }),
      'manualInputSelectBranchPath'));
  }

  return ({
    name: stepNameCurrent,
    screen: `${manualName}Input`,
    steps,
  });
};

const stepSelectAccountBank = (service, screensOrdering, configurations, stepName, returnStep) => ({
  name: getStepName('stepSelectAccountBank', service, stepName),
  screen: 'selectAccountBank',
  steps: [returnStep],
});

const stepSelectAccountBranch = (service, screensOrdering, configurations, stepName, returnStep) => ({
  name: getStepName('stepSelectAccountBranch', service, stepName),
  screen: 'selectAccountBranch',
  steps: [returnStep],
});

const stepOperator = (service, screensOrdering, configurations, stepName) => {
  if (!service.operator.customer_should_select_a_mobile_operator) {
    return getNextStep(service, screensOrdering, configurations, stepName);
  }
  return ({
    name: getStepName('stepSelectOperator', service, stepName),
    screen: 'selectOperator',
    steps: getNextStepsByOrder(service, screensOrdering, configurations, `${stepName}Operator`),
  });
};

const stepAmountPackage = (service, screensOrdering, configurations, stepName) => {
  const { amount_package } = service;
  if (amount_package.package_configuration === select_package_configuration_key.manual_amount) {
    return stepAmount(service, screensOrdering, configurations, stepName);
  }
  const stepSelectPackage = getStepName('stepSelectPackage', service, stepName);
  const steps = getNextStepsByOrder(service, screensOrdering, configurations, `${stepName}Package`);
  if (amount_package.show_account_balance) {
    steps.push(addActionPath(
      stepSelectDefaultAccount(service, screensOrdering, configurations, `${stepName}Package`, { name: stepSelectPackage }), 'selectPayingAccountPath',
    ));
  }
  if (amount_package.package_configuration === select_package_configuration_key.both) {
    steps.push(addActionPath(
      stepAmount(service, screensOrdering, configurations, `${stepName}Package`), 'amountInputPath',
    ));
  }
  return ({
    name: stepSelectPackage,
    screen: 'selectPackage',
    steps,
  });
};

const stepAmount = (service, screensOrdering, configurations, stepName) => {
  const isLoan = service.sub_category === loan;
  const steps = getNextStepsByOrder(service, screensOrdering, configurations, `${stepName}Amount`);
  const stepAmountInput = getStepName(isLoan ? 'stepAmountInputLoan' : 'stepAmountInput', service, stepName);
  const amount_configurations = service.amount || service.amount_package;
  if (amount_configurations.show_account_balance) {
    steps.push(addActionPath(
      stepSelectDefaultAccount(service, screensOrdering, configurations, `${stepName}Amount`, { name: stepAmountInput }), 'selectPayingAccountPath',
    ));
  }
  steps.push(addActionPath(stepSelectCurrency(service, screensOrdering, configurations, `${stepName}Amount`,
    { name: stepAmountInput }), 'selectTransferCurrencyPath'));
  if (isLoan) {
    return ({
      actionPath: 'amountInputPath',
      name: stepAmountInput,
      screen: 'amountInput',
      steps,
    });
  }
  return ({
    name: stepAmountInput,
    screen: 'amountInput',
    steps,
  });
};

const stepSelectDefaultAccount = (service, screensOrdering, configurations, stepName, returnStep) => ({
  name: getStepName('stepSelectDefaultAccount', service, stepName),
  screen: 'selectTransactionAccount',
  steps: [returnStep],
});

const stepSelectCurrency = (service, screensOrdering, configurations, stepName, returnStep) => ({
  name: getStepName('stepSelectCurrency', service, stepName),
  screen: 'selectTransferCurrency',
  steps: [returnStep],
});

const stepReferenceMain = (service, screensOrdering, configurations, stepName) => {
  const { reference } = service;
  if (!reference.step_enabled) {
    return getNextStep(service, screensOrdering, configurations, stepName);
  }
  const steps = [];
  if (reference.customers_add_photo_enabled) {
    steps.push(addActionPath(stepSelectReferenceImage(service, screensOrdering, configurations, stepName), 'selectReferenceImagePath'));
  }
  if (!reference.select_reason_from_options) {
    steps.push(getNextStep(service, screensOrdering, configurations, `${stepName}ReferenceInput`));
    return ({
      name: getStepName('stepReferenceInput', service, stepName),
      screen: 'referenceInput',
      steps,
    });
  }
  steps.push(stepSelectReference(service, screensOrdering, configurations, stepName));
  steps.push(addActionPath(getNextStep(service, screensOrdering, configurations, `${stepName}Reference`), 'confirmReferencePath'));
  return ({
    name: getStepName('stepReferenceMain', service, stepName),
    screen: 'referenceMain',
    steps,
  });
};

const stepSelectReference = (service, screensOrdering, configurations, stepName) => {
  const { reference } = service;
  const steps = [];
  if (reference.custom_reason) {
    steps.push(addActionPath(stepReferenceInput(service, screensOrdering, configurations, stepName), 'referenceInputPath'));
  }
  steps.push({ name: getStepName('stepReferenceMain', service, stepName) });
  return ({
    name: getStepName('stepSelectReference', service, stepName),
    screen: 'selectReference',
    steps,
  });
};

const stepReferenceInput = (service, screensOrdering, configurations, stepName) => ({
  name: getStepName('stepReferenceInput', service, stepName),
  screen: 'referenceInput',
  steps: [{ name: getStepName('stepReferenceMain', service, stepName) }],
});

const stepSelectReferenceImage = (service, screensOrdering, configurations, stepName) => ({
  name: getStepName('stepSelectReferenceImage', service, stepName),
  screen: 'selectReferenceImage',
  steps: service.reference.select_reason_from_options
    ? [{ name: getStepName('stepReferenceMain', service, stepName) }]
    : [{ name: getStepName('stepReferenceInput', service, stepName) }],
});

const stepSplitConfirmation = (service, screensOrdering, configurations, stepName) => ({
  name: getStepName('stepConfirmation', service, stepName),
  screen: 'splitPaymentConfirmation',
  steps: getNextStepsByOrder(service, screensOrdering, configurations, stepName),
});

/*
* General
* */
const stepConfirmation = (service, screensOrdering, configurations, stepName) => ({
  name: getStepName('stepConfirmation', service, stepName),
  screen: 'transferConfirmation',
  steps: getNextStepsByOrder(service, screensOrdering, configurations, stepName),
});

const stepInstallmentsNumberInputLoan = (service, screensOrdering, configurations, stepName, returnStep) => ({
  name: getStepName('stepInstallmentsNumberInputLoan', service, stepName),
  screen: 'installmentsNumberInput',
  steps: getNextStepsByOrder(service, screensOrdering, configurations, stepName, returnStep),
});

const stepInstallmentsOptionsLoan = (service, screensOrdering, configurations, stepName, returnStep) => ({
  name: getStepName('stepInstallmentsOptionsLoan', service, stepName),
  screen: 'selectPackage',
  steps: getNextStepsByOrder(service, screensOrdering, configurations, stepName, returnStep),
});

const stepSelectAccount = (service, screensOrdering, configurations, stepName, returnStep) => ({
  name: getStepName('stepSelectAccount', service, stepName),
  screen: 'selectTransactionAccount',
  steps: getNextStepsByOrder(service, screensOrdering, configurations, stepName, returnStep),
});

const stepSelectSuccess = (service, screensOrdering, configurations, stepName, returnStep) => {
  const steps = [];
  if (service.success.enable_customer_to_share_transaction
    && service.success.sharing_method === receipt) {
    steps.push(addActionPath(stepSelectReceipt(service, stepName), 'receiptPath'));
  }
  if (service.success.enable_customer_to_make_another_transfer && configurations.services.menu.layout_menu_screen) {
    steps.push(returnStep || {
      name: 'stepMenu',
      actionPath: 'menuPath',
    });
  }

  return ({
    name: getStepName('stepSuccess', service, stepName),
    isAuthNeeded: configurations.launch.auth.auth_upon_transactions
      && service.auth_within,
    screen: 'transactionSuccess',
    steps,
  });
};

const stepSelectReceipt = (service, stepName) => ({
  name: getStepName('stepReceipt', service, stepName),
  screen: 'transactionReceipt',
});

const getNextStepsByOrder = (service, screensOrdering, configurations, stepName = '', returnStep) => {
  if (screensOrdering[0]) {
    return [getNextStep(service, screensOrdering, configurations, stepName, returnStep)];
  }
  return [];
};

const getNextStep = (service, screensOrdering, configurations, stepName, returnStep) => getStepByStepKey[screensOrdering[0]](
  service, screensOrdering.slice(1), configurations, stepName, returnStep,
);

const getStepName = (currentName, service, previousStepName = '') => `${currentName}_${previousStepName}${getFlowCode(service)}`;

const addActionPath = (stepObject, actionPath) => ({
  actionPath, ...stepObject,
});

const getStepByStepKey = {
  recipient: stepTransferSelectRecipient,
  operator: stepOperator,
  amount_package: stepAmountPackage,
  amount: stepAmount,
  source: stepSelectAccount,
  number: stepInstallmentsNumberInputLoan,
  option: stepInstallmentsOptionsLoan,
  reference: stepReferenceMain,
  confirmation: stepConfirmation,
  success: stepSelectSuccess,
  balance: stepSelectAccount,
  transactions: stepAccountTransactionsDetails,
  select_bill: stepSelectBill,
  confirmation_bill: stepBillConfirmation,
  confirmation_split: stepSplitConfirmation,
};
